import { Temporal } from '@js-temporal/polyfill'
import { type Prisma } from '@prisma/client'
import {
	json,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { useLoaderData, Link } from '@remix-run/react'
import z from 'zod'
import { Container, H1, H2, P, Button, EventCard } from '#app/ui/components'
import { EventFilters, useEventFilters } from '#app/ui/sections/event-filters'
import { appName } from '#app/utils/constants.ts'
import {
	getEventOccurrenceDateRange,
	filterOccurrencesWithNestedTimezone,
} from '#app/utils/date-filter.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getEventOccurrenceTimeStatus } from '#app/utils/event.server.ts'
import { getTicketAvailabilityStatus } from '#app/utils/ticket-availability.ts'
import {
	isEventOccurrenceBookableByDate,
	getEventCutoffInstant,
} from '#app/utils/ticket.server.ts'
import mixtable from './mixtable.png'
import turnTableCloseup from './turntable-closeup.png'
import turnTable from './turntable.png'

export const meta: MetaFunction = () => [
	{ title: `${appName} - Discover Amazing Night Events` },
	{
		name: `description`,
		content: `Find and book tickets for the hottest nightlife events in your city. Exclusive clubs, live music, and unforgettable nights await!`,
	},
]

const searchParamsSchema = z.object({
	search: z.string().max(100).default(``),
	genre: z
		.string()
		.default(``)
		.transform((s) =>
			s
				.split(`,`)
				.map((g) => g.trim())
				.filter(Boolean),
		)
		.pipe(z.array(z.string().min(1).max(50)).max(10)),
	city: z
		.string()
		.default(``)
		.transform((val) =>
			val
				.split(`,`)
				.map((c) => c.trim())
				.filter((c) => c.length > 0),
		)
		.pipe(z.array(z.string().min(1).max(100)).max(5)),
	date: z
		.string()
		.regex(
			/^\d{4}-\d{2}-\d{2}$/,
			`Invalid date format. It should be YYYY-MM-DD.`,
		)
		.refine((d) => {
			try {
				Temporal.PlainDate.from(d)
				return true
			} catch {
				return false
			}
		}, `Invalid calendar date`)
		.optional(),
})

type EventSearchParams = z.infer<typeof searchParamsSchema>

function buildEventWhereClause(
	filters: EventSearchParams,
	options: { nightclubId?: string } = {},
): Prisma.EventWhereInput {
	const { search, genre, city } = filters
	const { nightclubId } = options

	const eventWhereClause: Prisma.EventWhereInput = {}

	// Filter by nightclub (for nightclub-specific event pages)
	if (nightclubId) {
		eventWhereClause.nightclubId = nightclubId
	}

	// Search by text
	if (search) {
		eventWhereClause.OR = [
			{ name: { contains: search } }, // Search by event name
			{ description: { contains: search } }, // Search by event description
			{ nightclub: { name: { contains: search } } }, // Search by nightclub name
		]
	}

	// Search by genres
	if (genre.length > 0) {
		eventWhereClause.genres = {
			some: {
				name: { in: genre },
			},
		}
	}

	// Search by cities - use exact match on city field
	if (city.length === 1) {
		// Single city filter
		if (!eventWhereClause.nightclub) {
			eventWhereClause.nightclub = {}
		}
		eventWhereClause.nightclub.city = city[0]
	} else if (city.length > 1) {
		// If multiple cities, we'll use OR to match any
		eventWhereClause.OR = [
			...(eventWhereClause.OR || []),
			...city.map((cityName) => ({
				nightclub: { city: cityName },
			})),
		]
	}

	return eventWhereClause
}

function buildOccurrenceWhereClause(
	date?: string,
): Prisma.EventOccurrenceWhereInput {
	// Only include events whose end time is at least 2 hours in the future
	// This ensures we don't show events that are about to end or have ended
	const cutoffInstant = getEventCutoffInstant()
	const cutoffDate = new Date(cutoffInstant.epochMilliseconds)

	const occurrenceWhereClause: Prisma.EventOccurrenceWhereInput = {
		endDate: {
			gte: cutoffDate, // Database uses UTC dates, so this works for all timezones
		},
	}

	// Filter by date if provided, using a timezone-aware buffer range
	if (date) {
		const { startDate, endDate } = getEventOccurrenceDateRange(date)
		occurrenceWhereClause.startDate = {
			gte: startDate,
			lt: endDate,
		}
	}

	return occurrenceWhereClause
}

type EventOccurrenceWithRelations = {
	id: string
	startDate: Date
	endDate: Date
	cutoffHours: number
	capacity: number | null
	event: {
		id: string
		nightclub: {
			id: string
			timezone: string
			capacity: number
		}
		ticketTypes: Array<{
			id: string
			isPaid: boolean
			basePrice: number | null
			defaultQuantity: number
			_count: {
				purchasedTickets: number
			}
		}>
	}
}

export type TicketStats = {
	totalAvailable: number
	totalSold: number
	availableNow: number
	minPrice: number | null
	maxPrice: number
	hasPaidTickets: boolean
	hasFreeTickets: boolean
}

export type EnrichedEventOccurrence<T extends EventOccurrenceWithRelations> =
	T & {
		isBookable: boolean
		capacity: number
		ticketStats: TicketStats
		utilizationPercentage: number
		hasTickets: boolean
		isSoldOut: boolean
		timeStatus: `upcoming` | `ongoing` | `past`
	}

async function enrichEventOccurrences<T extends EventOccurrenceWithRelations>(
	occurrences: T[],
): Promise<EnrichedEventOccurrence<T>[]> {
	return Promise.all(
		occurrences.map(async (occurrence) => {
			const isBookable = isEventOccurrenceBookableByDate(
				occurrence.startDate,
				occurrence.endDate,
				occurrence.cutoffHours,
			)
			const capacity =
				occurrence.capacity || occurrence.event.nightclub.capacity

			// Calculate ticket availability from event-level ticket types
			const ticketStats = occurrence.event.ticketTypes.reduce(
				(stats, ticketType) => {
					const sold = ticketType._count.purchasedTickets
					const available = ticketType.defaultQuantity - sold

					stats.totalAvailable += ticketType.defaultQuantity
					stats.totalSold += sold
					stats.availableNow += available

					if (ticketType.isPaid) {
						stats.minPrice =
							stats.minPrice === null
								? ticketType.basePrice
								: Math.min(stats.minPrice!, ticketType.basePrice || 0)
						stats.maxPrice = Math.max(stats.maxPrice, ticketType.basePrice || 0)
						stats.hasPaidTickets = true
					} else {
						stats.hasFreeTickets = true
					}

					return stats
				},
				{
					totalAvailable: 0,
					totalSold: 0,
					availableNow: 0,
					minPrice: null as number | null,
					maxPrice: 0,
					hasPaidTickets: false,
					hasFreeTickets: false,
				},
			)

			const utilizationPercentage =
				ticketStats.totalAvailable > 0
					? Math.round(
							(ticketStats.totalSold / ticketStats.totalAvailable) * 100,
						)
					: 0

			// Calculate time status for event timing badge
			const timeStatus = getEventOccurrenceTimeStatus(occurrence)

			return {
				...occurrence,
				isBookable,
				capacity,
				ticketStats,
				utilizationPercentage,
				hasTickets: occurrence.event.ticketTypes.length > 0,
				isSoldOut:
					ticketStats.availableNow === 0 &&
					occurrence.event.ticketTypes.length > 0,
				timeStatus,
			}
		}),
	)
}

async function getPlatformStats() {
	// Only count events whose end time is at least 2 hours in the future
	const cutoffDate = new Date(getEventCutoffInstant().epochMilliseconds)

	const [upcomingEvents, totalVenues, totalGenres] = await Promise.all([
		prisma.eventOccurrence.count({
			where: {
				endDate: {
					gte: cutoffDate, // Database uses UTC dates, so this works for all timezones
				},
			},
		}),
		prisma.nightclub.count(),
		prisma.genres.count(),
	])

	return {
		upcomingEvents,
		totalVenues,
		totalGenres,
	}
}

export async function loader({ request }: LoaderFunctionArgs) {
	// Parse and validate search parameters with Zod
	const filters = searchParamsSchema.parse(
		Object.fromEntries(new URL(request.url).searchParams),
	)

	// Build the where clauses using shared utilities
	const eventWhereClause = buildEventWhereClause(filters)
	const occurrenceWhereClause = buildOccurrenceWhereClause(filters.date)

	// Get all upcoming and ongoing event occurrences with comprehensive data
	const eventOccurrences = await prisma.eventOccurrence.findMany({
		where: {
			...occurrenceWhereClause,
			event: eventWhereClause,
		},
		include: {
			event: {
				include: {
					nightclub: {
						select: {
							id: true,
							name: true,
							address: true,
							latitude: true,
							longitude: true,
							capacity: true,
							timezone: true,
						},
					},
					genres: {
						select: {
							name: true,
						},
					},
					images: {
						select: {
							id: true,
							altText: true,
						},
						take: 1,
					},
					ticketTypes: {
						select: {
							id: true,
							name: true,
							defaultQuantity: true,
							isPaid: true,
							basePrice: true,
							_count: {
								select: {
									purchasedTickets: {
										where: {
											purchase: {
												status: `completed`,
											},
										},
									},
								},
							},
						},
					},
				},
			},
		},
		orderBy: [{ startDate: `asc` }],
		take: 100, // Fetch more events initially to account for date filtering
	})

	// Filter by date using nightclub timezone (include early morning events 0-6 AM)
	const filteredByDate = filterOccurrencesWithNestedTimezone(
		eventOccurrences,
		filters.date,
		true, // Include early morning events (0-6 AM next day)
	)

	// Enrich events with ticket availability and booking status
	const enrichedEvents = await enrichEventOccurrences(filteredByDate)

	// Filter out events without tickets and limit to 20 events
	const displayEvents = enrichedEvents
		.filter((event) => event.hasTickets)
		.slice(0, 20)

	// Get unique genres for filters
	const genres = await prisma.genres.findMany({
		orderBy: { name: `asc` },
	})

	// Get unique cities for filters - using structured city data
	const nightclubCities = await prisma.nightclub.findMany({
		select: { city: true },
		where: { city: { not: null } },
		distinct: [`city`],
		orderBy: { city: `asc` },
	})

	const cities = nightclubCities
		.map((nc) => nc.city)
		.filter((city): city is string => city !== null)

	return json({
		events: displayEvents,
		genres,
		cities,
		stats: await getPlatformStats(),
		filters,
	})
}

export default function Index() {
	const { events, genres, cities, stats, filters } =
		useLoaderData<typeof loader>()

	// Use the shared event filters hook
	useEventFilters({
		initialSearch: filters.search,
		initialDate: filters.date,
		initialGenres: filters.genre,
		initialCities: filters.city,
	})

	const getEventStatus = (event: (typeof events)[0]) => {
		const now = new Date()
		const startDate = new Date(event.startDate)
		const endDate = new Date(event.endDate)
		const isOngoing = startDate <= now && now < endDate

		// Check if still bookable (using the same logic as our server function)
		const cutoffTimeMs = endDate.getTime() - event.cutoffHours * 60 * 60 * 1000
		const cutoffTime = new Date(cutoffTimeMs)
		const isStillBookable = now < cutoffTime

		if (isOngoing && isStillBookable) {
			return {
				text: `LIVE NOW`,
				color: `red`,
				priority: 1,
				isLive: true,
				description: `Event is happening now! Book your tickets before it's too late`,
			}
		}

		if (!event.hasTickets) {
			return {
				text: `No tickets`,
				color: `gray`,
				priority: 6,
				isLive: false,
				description: `This event doesn't offer tickets`,
			}
		}

		// Use centralized ticket availability logic for consistent thresholds
		const availability = getTicketAvailabilityStatus({
			availableNow: event.ticketStats.availableNow,
			totalAvailable: event.ticketStats.totalAvailable,
		})

		// Map availability status to priority for sorting
		const priorityMap = {
			'sold-out': 5,
			'few-left': 2,
			'selling-fast': 3,
			'available': 4,
		}

		const descriptionMap = {
			'sold-out': `All tickets have been sold`,
			'few-left': `Only ${event.ticketStats.availableNow} tickets remaining`,
			'selling-fast': `Tickets are selling quickly`,
			'available': `Tickets available for this event`,
		}

		return {
			text: availability.text,
			color: availability.color,
			priority: priorityMap[availability.status],
			isLive: false,
			description: descriptionMap[availability.status],
		}
	}

	return (
		<div>
			{/* Hero Section */}
			<section>
				{/* Background video */}
				<video
					className="absolute inset-0 z-0 h-screen w-screen object-cover"
					autoPlay
					muted
					loop
					playsInline
					src="/videos/homepage-hero-background.mp4"
				/>
				<Container className="relative z-10">
					<div className="flex min-h-[calc(100vh-104px-24px)] flex-col justify-between py-10 lg:py-14">
						<div className="text-center">
							<div className="mb-6 mt-14 flex justify-center text-2xl font-medium text-brand-muted-amethyst">
								Live Events Platform
							</div>
							<H1 className="mb-6 text-4xl font-bold text-white lg:text-8xl">
								Discover Amazing
								<br />
								Night Events
							</H1>
						</div>
						<div>
							{/* Platform Stats */}
							<div
								className="mb-12 flex flex-col items-center justify-center gap-4 text-white lg:flex-row lg:gap-8"
								role="region"
								aria-label="Platform Statistics"
							>
								<div className="grid w-full grid-cols-2 gap-10 sm:flex sm:flex-1 sm:flex-row sm:items-center">
									<div className="flex flex-1 flex-col items-center">
										<div
											className="text-4xl lg:text-6xl"
											aria-label="Total events count"
										>
											{stats.upcomingEvents}
										</div>
										<div className="whitespace-nowrap text-xl">
											Total Events
										</div>
									</div>
									<svg
										className="hidden h-2 w-2 text-brand-muted-amethyst lg:block"
										fill="currentColor"
										viewBox="0 0 8 8"
										xmlns="http://www.w3.org/2000/svg"
										aria-hidden="true"
									>
										<circle cx="4" cy="4" r="4" />
									</svg>

									<div className="flex flex-1 flex-col items-center">
										<div
											className="text-4xl lg:text-6xl"
											aria-label="Total venues count"
										>
											{stats.totalVenues}
										</div>
										<div className="whitespace-nowrap text-xl">Venues</div>
									</div>
									<svg
										className="hidden h-2 w-2 text-brand-muted-amethyst lg:block"
										fill="currentColor"
										viewBox="0 0 8 8"
										xmlns="http://www.w3.org/2000/svg"
										aria-hidden="true"
									>
										<circle cx="4" cy="4" r="4" />
									</svg>

									<div className="flex flex-1 flex-col items-center">
										<div
											className="text-4xl lg:text-6xl"
											aria-label="Total genres count"
										>
											{stats.totalGenres}
										</div>
										<div className="whitespace-nowrap text-xl">Genres</div>
									</div>
									</div>
							</div>
						</div>
					</div>
				</Container>
				{/* Floating Arrow */}
				<div className="absolute bottom-2 left-1/2 -translate-x-1/2">
					<svg
						className="h-8 w-8 animate-bounce text-white"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M19 9l-7 7-7-7"
						/>
					</svg>
				</div>
			</section>

			{/* Events Grid */}
			<section id="events" className="bg-brand-lighter-background py-12">
				<Container>
					<H2>Events</H2>
					<EventFilters
						genres={genres}
						cities={cities}
						initialFilters={filters}
					/>
					{events.length === 0 ? (
						<div className="py-16 text-center">
							<H2 className="mx-auto mb-4 text-2xl font-bold">
								No events found
							</H2>
							<P className="mx-auto mb-6 text-brand-grey-light-2">
								Clear your search filters to see more events.
							</P>
						</div>
					) : (
						<div
							key={`${filters.date}-${filters.search}-${filters.genre.join(`,`)}-${filters.city.join(`,`)}`}
							className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
						>
							{events
								.sort((a, b) => {
									// Sort by status priority first (live events at top), then by date
									const statusA = getEventStatus(a)
									const statusB = getEventStatus(b)
									if (statusA.priority !== statusB.priority) {
										return statusA.priority - statusB.priority
									}
									return (
										new Date(a.startDate).getTime() -
										new Date(b.startDate).getTime()
									)
								})
								.map((event, index) => (
									<EventCard
										key={event.id}
										variant="grid"
										occurrence={event as any}
										isFlipped={index % 2 === 0}
									/>
								))}
						</div>
					)}
				</Container>
			</section>

			{/* CTA Section */}
			<section className="relative min-h-screen py-16">
				{/* Background Grid */}
				<div className="absolute inset-0 bg-[linear-gradient(to_right,#42184b_1px,transparent_1px),linear-gradient(to_bottom,#42184b_1px,transparent_1px)] bg-[size:14rem_14rem] [background-position:7rem_7rem]" />
				<Container className="relative z-10 overflow-hidden">
					<div className="flex flex-col items-center justify-center gap-28">
						<p className="mt-10 max-w-3xl text-center text-4xl">
							Find and book tickets for the hottest nightlife events in your
							city. Exclusive clubs, live music, and unforgettable nights await!
						</p>
						<div className="flex items-center justify-center gap-10">
							{/* Left SVG */}
							<div className="h-64 w-auto flex-shrink-0">
								<svg
									viewBox="0 0 117 200"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="h-full w-auto"
									preserveAspectRatio="xMidYMid meet"
								>
									<path
										d="M29.2893 170.711C48.043 189.464 73.4783 200 100 200C11.5 115 69 29.2893 100 0C73.4783 4.00466e-06 48.0429 10.5357 29.2893 29.2893C10.5357 48.043 -2.00233e-06 73.4784 0 100C2.00233e-06 126.522 10.5357 151.957 29.2893 170.711Z"
										fill="#4F0C5E"
									/>
									<path
										d="M18.25 100C18.25 126.19 28.6536 151.308 47.1729 169.827C64.9537 187.608 88.8176 197.905 113.873 198.698C71.3199 156.638 63.8123 114.389 70.9619 78.8623C77.8698 44.5378 98.426 16.6309 113.861 1.30078C88.8102 2.09663 64.951 12.3947 47.1729 30.1729C28.6536 48.6921 18.25 73.8099 18.25 100Z"
										stroke="white"
										strokeWidth="2.5"
									/>
								</svg>
							</div>

							{/* Left semi-circle image */}
							<div className="relative h-64 w-32 overflow-hidden">
								<div className="absolute inset-0 h-64 w-64 overflow-hidden rounded-full">
									<img
										src={turnTable}
										alt="Turn table"
										className="h-full w-full object-cover"
									/>
								</div>
							</div>

							{/* Center circle image */}
							<div className="relative">
								<div className="relative h-64 w-64 overflow-hidden rounded-full">
									<img
										src={turnTableCloseup}
										alt="Turn table closeup"
										className="h-full w-full object-cover"
									/>
								</div>
								<svg
									width="200"
									height="200"
									viewBox="0 0 200 200"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="left--right-5 absolute -right-5 bottom-0 top-0 h-full w-full"
								>
									<circle
										cx="100"
										cy="100"
										r="98.75"
										stroke="white"
										strokeWidth="2.5"
									/>
								</svg>
							</div>

							{/* Right semi-circle image */}
							<div className="relative h-64 w-32 overflow-hidden">
								<div className="absolute right-0 h-64 w-64 overflow-hidden rounded-full">
									<img
										src={mixtable}
										alt="Mixtable"
										className="h-full w-full object-cover"
									/>
								</div>
							</div>

							{/* Right SVG */}
							<div className="h-64 w-auto flex-shrink-0">
								<svg
									viewBox="0 0 115 200"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="h-full w-auto"
									preserveAspectRatio="xMidYMid meet"
								>
									<path
										d="M70.7107 170.711C51.957 189.464 26.5217 200 0 200C88.5 115 31 29.2893 1.52588e-05 0C26.5217 4.00466e-06 51.9571 10.5357 70.7107 29.2893C89.4643 48.043 100 73.4784 100 100C100 126.522 89.4643 151.957 70.7107 170.711Z"
										fill="#964000"
									/>
									<path
										d="M113.75 100C113.75 126.19 103.346 151.308 84.8271 169.827C67.0463 187.608 43.1824 197.905 18.127 198.698C60.6801 156.638 68.1877 114.389 61.0381 78.8623C54.1302 44.5378 33.574 16.6309 18.1387 1.30078C43.1898 2.09663 67.049 12.3947 84.8271 30.1729C103.346 48.6921 113.75 73.8099 113.75 100Z"
										stroke="white"
										strokeWidth="2.5"
									/>
								</svg>
							</div>
						</div>
						<div>
							<H2 className="mb-14 max-w-4xl text-center text-6xl">
								Ready to Buy
								<br />a Ticket for your dream event?
							</H2>
							<p className="max-w-3xl text-center text-2xl">
								Join our platform and discover thousands of unforgettable
								nights!
							</p>
							<div className="flex justify-center">
								<Button
									asChild
									variant="default"
									size="lg"
									className="mt-10 bg-brand-deep-pink px-14 py-8 text-xl font-light text-brand-grey-light-1"
								>
									<Link to="/#events">Explore Events</Link>
								</Button>
							</div>
						</div>
					</div>
				</Container>
			</section>
		</div>
	)
}
